import type { ReactElement } from 'react'

type Obj = Record<string, unknown>
const object = (value: unknown): Obj =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Obj) : {}
const scalar = (value: unknown): string =>
  typeof value === 'number'
    ? String(Number.isInteger(value) ? value : Number(value.toPrecision(5)))
    : typeof value === 'string'
      ? value
      : '—'
const feature = (value: unknown, rank: unknown): string => {
  const f = object(value)
  return f.state === 'present' ? `${scalar(f.value)} (${scalar(rank)})` : scalar(f.state)
}

/** Only recorded telemetry is presented as fact; old or truncated traces remain unknown.
 * Like StepInspector, this developer-forensics view uses English labels alongside verbatim
 * trace vocabulary. Localization should cover the inspector together, not translate provenance.
 */
export function RankingView({
  payload,
  truncated,
}: {
  payload: unknown
  truncated: boolean
}): ReactElement {
  if (truncated) return <p>Ranking details require the full payload.</p>
  const data = object(payload)
  if (data.rankingTraceVersion !== 1)
    return (
      <p>
        Ranking stages not recorded here. Check separate ranking steps when available; older traces
        cannot show which stages ran.
      </p>
    )
  return (
    <div className="space-y-3 text-xs" data-testid="ranking-view">
      <p>
        <strong>{scalar(data.stage)}</strong>: {scalar(data.status)}
        {data.method === 'rrf'
          ? ` · RRF (k=${scalar(data.rrfK)})`
          : data.method === 'bm25'
            ? ' · BM25 only'
            : ''}
        {data.scopePartition ? ` · partition: ${scalar(data.scopePartition)}` : ''}
        {data.reason ? ` · ${scalar(data.reason)}` : ''}
      </p>
      {Boolean(data.dense) && (
        <p>
          Dense retrieval: {scalar(data.dense)}. BM25 candidates: {scalar(data.bm25Count)}; dense
          candidates: {scalar(data.denseCount)}.
        </p>
      )}
      {Boolean(data.ordering) && <p>{scalar(data.ordering)}</p>}
      <p className="text-muted-foreground">
        Path: {scalar(data.path)} · call: {scalar(data.callId)}
      </p>
      {data.before ? (
        <CandidateTable title="Before cross-encoder" data={object(data.before)} />
      ) : null}
      {data.orderUnchanged === true && <p>Order unchanged; cross-encoder skipped.</p>}
      {data.after ? <CandidateTable title="After cross-encoder" data={object(data.after)} /> : null}
      {data.stage !== 'cross_encoder' && data.candidates ? (
        <CandidateTable title="Candidates at this stage" data={data} />
      ) : null}
      <p className="text-muted-foreground">
        Positions are local to this stage. Named records, entity mentions, scope priority, duplicate
        collapse and evidence budgets can affect final selection.
      </p>
    </div>
  )
}

function CandidateTable({ title, data }: { title: string; data: Obj }): ReactElement {
  const rows = Array.isArray(data.candidates) ? data.candidates : []
  return (
    <div className="overflow-x-auto">
      <p>
        {title}: showing {scalar(data.captured)} of {scalar(data.total)}
      </p>
      <table className="w-full text-left text-xs">
        <thead>
          <tr>
            {[
              'Position',
              'Record / component',
              'Partition',
              'BM25 (rank)',
              'Cosine (rank)',
              'Fusion',
              'Cross-encoder',
            ].map((h) => (
              <th className="p-1" key={h}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((value, i) => {
            const row = object(value),
              ranking = object(row.ranking)
            return (
              <tr key={i}>
                <td className="p-1">{scalar(row.position)}</td>
                <td className="p-1 break-all">
                  {scalar(row.record)} / {scalar(row.component)}
                </td>
                <td className="p-1">{scalar(ranking.scopePartition)}</td>
                <td className="p-1">{feature(ranking.bm25, ranking.bm25Rank)}</td>
                <td className="p-1">{feature(ranking.cosine, ranking.cosineRank)}</td>
                <td className="p-1">{scalar(ranking.fusionScore)}</td>
                <td className="p-1">{scalar(row.rerank)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
